import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';
import { Globe, Palette, Smartphone, Search, Megaphone, Cpu, Zap, DollarSign, Target, ShieldCheck, Handshake, ExternalLink } from 'lucide-react';

// ✅ Blog images — direct imports
import imgWebDev      from '../data/bimages/Codeviora-blog-1.webp';
import imgAppDev      from '../data/bimages/Codeviora-blog-2.webp';
import imgDesign      from '../data/bimages/Codeviora-blog-3.webp';
import imgSEO         from '../data/bimages/Codeviora-blog-4.webp';
import imgMarketing   from '../data/bimages/Codeviora-blog-5.webp';
import imgAI          from '../data/bimages/Codeviora-blog-6.webp';

const BLOG_IMAGES = {
  'Web Development'        : imgWebDev,
  'App Development'        : imgAppDev,
  'Web Design'             : imgDesign,
  'SEO'                    : imgSEO,
  'Digital Marketing'      : imgMarketing,
  'Artificial Intelligence': imgAI,
};

const FALLBACK_IMAGE = imgWebDev;

const SERVICES = [
  { icon:<Globe size={28} />, title:'Web Development', desc:'Fast, scalable websites and web apps — from landing pages to full platforms. Built to convert.', tags:['React','Next.js','WordPress','Shopify'] },
  { icon:<Palette size={28} />, title:'Web Design', desc:'Premium UI/UX design that reflects your brand and drives action. Every pixel has a purpose.', tags:['UI/UX','Figma','Branding','Prototyping'] },
  { icon:<Smartphone size={28} />, title:'App Development', desc:'Cross-platform mobile apps for iOS & Android — beautiful, fast, and built to scale.', tags:['React Native','Flutter','iOS','Android'] },
  { icon:<Search size={28} />, title:'SEO', desc:'Data-driven SEO that improves rankings and drives consistent organic traffic.', tags:['On-Page','Technical SEO','Local SEO','Analytics'] },
  { icon:<Megaphone size={28} />, title:'Digital Marketing', desc:'Full-funnel marketing across Google, Meta, social, and email — turning clicks into customers.', tags:['Google Ads','Meta Ads','Social Media','Email'] },
  { icon:<Cpu size={28} />, title:'AI & Automation', desc:'AI-powered tools, chatbots, and automation systems that save time and give you a competitive edge.', tags:['AI Chatbots','Automation','AI Content','Integrations'] },
];

const MARQUEE_ITEMS = ['Web Development','Web Design','App Development','SEO','Digital Marketing','AI & Automation','React','Next.js','WordPress','Shopify','React Native','Flutter'];

const TESTIMONIALS = [
  { quote:"Codeviora transformed our online presence completely. Their attention to detail and technical expertise is unmatched.", name:"Paul S", role:"Founder, UK" },
  { quote:"Our website traffic doubled after CodeViora's SEO work. They know exactly what they're doing — results speak for themselves.", name:"Yvvone V", role:"CEO, Bossing Up" },
  { quote:"The app they built for us has 50,000+ downloads. Clean code, beautiful design, and they were available whenever we needed them.", name:"Arjun Mehta", role:"Co-founder, INDIA" },
  { quote:"Best investment we made for our business. The website is getting compliments from every client we show it to.", name:"Geo", role:"Director, AUS" },
  { quote:"Their digital marketing campaigns brought us qualified leads we had never seen before. ROI was 6x within 3 months.", name:"Josh K", role:"MD, UK" },
];

const STATS = [
  { value: 100, suffix: '+', label: 'Projects Completed' },
  { value: 25,  suffix: '+', label: 'Happy Clients' },
  { value: 10,  suffix: '+', label: 'Years Experience' },
  { value: 98,  suffix: '%', label: 'Client Retention' },
];

const WHY_US = [
  {icon:<Zap size={32} />,title:'Fast Delivery',desc:'Typical projects launch in 2–6 weeks without cutting corners.'},
  {icon:<DollarSign size={32} />,title:'Transparent Pricing',desc:'No hidden fees. Clear quotes before we start anything.'},
  {icon:<Target size={32} />,title:'Result Focused',desc:'We measure success by traffic, leads, and revenue — not hours.'},
  {icon:<ShieldCheck size={32} />,title:'Post-Launch Support',desc:'Bug fixes, updates, and improvements included after launch.'},
  {icon:<Handshake size={32} />,title:'Dedicated Team',desc:'A dedicated point of contact, not a ticket system.'},
  {icon:<Cpu size={32} />,title:'AI Powered',desc:'We use AI tools to deliver faster, smarter, and better results.'},
];

// ── Animated Counter ──────────────────────────────────────
function AnimatedCounter({ value, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60;
        const increment = value / steps;
        let current = 0;
        const timer = setInterval(() => {
          current += increment;
          if (current >= value) { setCount(value); clearInterval(timer); }
          else setCount(Math.floor(current));
        }, 1800 / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ── Typewriter ────────────────────────────────────────────
function Typewriter({ texts }) {
  const [textIndex, setTextIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pause, setPause] = useState(false);
  useEffect(() => {
    if (pause) { const t = setTimeout(() => setPause(false), 1800); return () => clearTimeout(t); }
    const current = texts[textIndex];
    if (!deleting && displayed === current) { setPause(true); setDeleting(true); return; }
    if (deleting && displayed === '') { setDeleting(false); setTextIndex(i => (i + 1) % texts.length); return; }
    const t = setTimeout(() => {
      setDisplayed(prev => deleting ? prev.slice(0, -1) : current.slice(0, prev.length + 1));
    }, deleting ? 40 : 80);
    return () => clearTimeout(t);
  }, [displayed, deleting, textIndex, texts, pause]);
  return <span>{displayed}<span className="animate-pulse text-white/60">|</span></span>;
}

// ── AI Estimator ──────────────────────────────────────────
function AIEstimator() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const questions = [
    { q:'What do you need?', key:'service', options:[
      {label:'🌐 Website',value:'website'},{label:'📱 Mobile App',value:'app'},
      {label:'🔍 SEO',value:'seo'},{label:'📣 Digital Marketing',value:'marketing'},
      {label:'🤖 AI Tool',value:'ai'},{label:'🎨 Design Only',value:'design'},
    ]},
    { q:'What is your business size?', key:'size', options:[
      {label:'👤 Solo / Startup',value:'solo'},{label:'🏢 Small Business',value:'small'},
      {label:'🏬 Mid-size Company',value:'mid'},{label:'🏭 Enterprise',value:'enterprise'},
    ]},
    { q:'What is your timeline?', key:'timeline', options:[
      {label:'⚡ ASAP (1–2 weeks)',value:'asap'},{label:'📅 1 Month',value:'month'},
      {label:'🗓️ 2–3 Months',value:'quarter'},{label:'🕐 Flexible',value:'flexible'},
    ]},
  ];

  const getEstimate = (ans) => {
    const prices = {
      website:{solo:'₹8K–₹20K',small:'₹20K–₹50K',mid:'₹50K–₹1.5L',enterprise:'₹1.5L+'},
      app:{solo:'₹25K–₹60K',small:'₹60K–₹1.5L',mid:'₹1.5L–₹4L',enterprise:'₹4L+'},
      seo:{solo:'₹5K/mo',small:'₹10K/mo',mid:'₹20K/mo',enterprise:'₹40K+/mo'},
      marketing:{solo:'₹8K/mo',small:'₹15K/mo',mid:'₹30K/mo',enterprise:'₹60K+/mo'},
      ai:{solo:'₹20K–₹60K',small:'₹60K–₹1.5L',mid:'₹1.5L–₹3L',enterprise:'₹3L+'},
      design:{solo:'₹5K–₹15K',small:'₹15K–₹40K',mid:'₹40K–₹1L',enterprise:'₹1L+'},
    };
    const timelines = {
      website:{asap:'1–2 wks',month:'2–4 wks',quarter:'1–2 mo',flexible:'2–4 wks'},
      app:{asap:'3–4 wks',month:'4–6 wks',quarter:'2–3 mo',flexible:'4–8 wks'},
      seo:{asap:'Ongoing',month:'Ongoing',quarter:'Ongoing',flexible:'Ongoing'},
      marketing:{asap:'Week 1',month:'Week 1',quarter:'Week 1',flexible:'Week 1'},
      ai:{asap:'2–4 wks',month:'4–8 wks',quarter:'2–3 mo',flexible:'4–8 wks'},
      design:{asap:'1 wk',month:'1–2 wks',quarter:'2–4 wks',flexible:'1–3 wks'},
    };
    return { price: prices[ans.service]?.[ans.size]||'₹10K–₹50K', time: timelines[ans.service]?.[ans.timeline]||'2–4 weeks' };
  };

  const handleOption = (key, value) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    if (step < questions.length - 1) setStep(s => s + 1);
    else setResult(getEstimate(newAnswers));
  };

  const reset = () => { setStep(0); setAnswers({}); setResult(null); };

  return (
    <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden h-full flex flex-col">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-white dark:bg-[#111] flex items-center justify-center flex-shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-black dark:text-white">
              <path d="M12 2a10 10 0 110 20A10 10 0 0112 2z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 12s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <p className="text-white text-xs font-medium">AI Project Estimator</p>
            <p className="text-white/30 text-xs">Get instant quote</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white/30 text-xs">Live</span>
        </div>
      </div>
      {!result && (
        <div className="h-0.5 bg-white/5 dark:bg-white/5">
          <div className="h-full bg-white dark:bg-[#111] transition-all duration-500" style={{width:`${(step/questions.length)*100}%`}} />
        </div>
      )}
      <div className="p-6 flex-1 flex flex-col justify-between">
        {!result ? (
          <>
            <div className="flex items-center gap-2 mb-5">
              {questions.map((_,i) => <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i<=step?'bg-white dark:bg-[#111]':'bg-white/15'}`} />)}
            </div>
            <div className="mb-5">
              <p className="text-white/40 text-xs mb-1">Step {step+1} of {questions.length}</p>
              <p className="text-white font-medium text-sm">{questions[step].q}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 flex-1">
              {questions[step].options.map(({label,value}) => (
                <button key={value} onClick={() => handleOption(questions[step].key, value)}
                  className="bg-white/5 dark:bg-white/5 border border-white/10 hover:border-white hover:bg-white/10 rounded-xl px-3 py-3 text-xs text-white/70 hover:text-white transition-all duration-200 text-left font-medium">
                  {label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center flex-1 gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-[#111] flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-black dark:text-white">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </div>
            <div>
              <p className="text-white/40 text-xs mb-1">Estimated Investment</p>
              <p className="text-3xl text-white font-black mb-1" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{result.price}</p>
              <p className="text-white/40 text-xs">Timeline: {result.time}</p>
            </div>
            <div className="w-full flex flex-col gap-2 mt-2">
              <Link to="/contact" className="w-full bg-white dark:bg-[#111] text-black dark:text-white text-xs font-medium py-3 rounded-xl hover:bg-white/90 transition-colors">Get Exact Quote →</Link>
              <button onClick={reset} className="text-white/30 text-xs hover:text-white transition-colors">Start over</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Service Carousel ──────────────────────────────────────
function ServiceCarousel() {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const reset = () => { clearInterval(timerRef.current); timerRef.current = setInterval(() => setActive(a => (a+1)%SERVICES.length), 4000); };
  useEffect(() => { reset(); return () => clearInterval(timerRef.current); }, []);
  const go = i => { setActive(i); reset(); };
  const s = SERVICES[active];
  return (
    <div className="bg-black rounded-2xl p-8 border border-white/10 h-full">
      <div className="flex items-center gap-2 mb-8">
        {SERVICES.map((_,i) => <button key={i} onClick={() => go(i)} className={`h-1 rounded-full transition-all duration-300 ${i===active?'bg-white dark:bg-[#111] w-8':'bg-white/20 w-4'}`} aria-label={`Service ${i+1}`} />)}
      </div>
      <div key={active} className="animate-fade-in">
        <span className="text-4xl mb-5 block">{s.icon}</span>
        <h3 className="text-2xl text-white mb-3" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{s.title}</h3>
        <p className="text-white/50 text-sm leading-relaxed mb-6">{s.desc}</p>
        <div className="flex flex-wrap gap-2">{s.tags.map(t => <span key={t} className="text-xs bg-white/5 dark:bg-white/5 text-white/60 border border-white/10 px-3 py-1 rounded-full">{t}</span>)}</div>
      </div>
      <div className="mt-8 flex gap-2">
        <button onClick={() => go((active-1+SERVICES.length)%SERVICES.length)} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:border-white hover:text-white transition-colors">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <button onClick={() => go((active+1)%SERVICES.length)} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:border-white hover:text-white transition-colors">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>
    </div>
  );
}

// ── Testimonial Carousel ──────────────────────────────────
function TestimonialCarousel() {
  const [active, setActive] = useState(0);
  useEffect(() => { const t = setInterval(() => setActive(a => (a+1)%TESTIMONIALS.length), 5000); return () => clearInterval(t); }, []);
  const t = TESTIMONIALS[active];
  return (
    <div>
      <div className="bg-black border border-white/10 rounded-2xl p-8 mb-5 relative overflow-hidden" style={{minHeight:200}}>
        <svg className="absolute top-5 left-7 opacity-20" width="48" height="36" viewBox="0 0 48 36" fill="white">
          <path d="M0 36V22.5C0 15 3 9 9 4.5L13.5 0l4.5 3C13.5 6 11 10 10.5 15H18V36H0zm27 0V22.5C27 15 30 9 36 4.5L40.5 0 45 3C40.5 6 38 10 37.5 15H45V36H27z"/>
        </svg>
        <div key={active} className="animate-fade-in relative z-10">
          <p className="text-white/70 text-base leading-relaxed mb-6">"{t.quote}"</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/10 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">{t.name.split(' ').map(n=>n[0]).join('')}</div>
            <div><p className="font-medium text-white text-sm">{t.name}</p><p className="text-white/40 text-xs">{t.role}</p></div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-center">
        {TESTIMONIALS.map((_,i) => <button key={i} onClick={() => setActive(i)} className={`h-1 rounded-full transition-all duration-300 ${i===active?'bg-white dark:bg-[#111] w-6':'bg-white/15 w-3'}`} aria-label={`Review ${i+1}`} />)}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────
export default function Home() {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const PROJECTS_HOME = [
    { url:require('../images/pf1.webp'), label:'WebToolSpark', tag:'Web Development' },
    { url:require('../images/pf2.webp'), label:'LetFind', tag:'Education Website' },
    { url:require('../images/pf3.3.png'), label:'Mind Sharper', tag:'Android App' },
    { url:require('../images/pf4.webp'), label:'Urbancrazy', tag:'WordPress Development' },
    { url:require('../images/pf5.webp'), label:'Diversiply', tag:'Open Cart' },
    { url:require('../images/pf6.webp'), label:'Larimarmagic', tag:'WordPress Development' },
  ];

  // ✅ Latest 3 blogs — id descending
  const latestBlogs = [...blogPosts].sort((a, b) => b.id - a.id).slice(0, 3);

  const prevImg = () => setLightbox(l => ({ ...l, index: (l.index - 1 + PROJECTS_HOME.length) % PROJECTS_HOME.length }));
  const nextImg = () => setLightbox(l => ({ ...l, index: (l.index + 1) % PROJECTS_HOME.length }));
  const closeImg = () => setLightbox({ open: false, index: 0 });

  useScrollReveal();

  return (
    <>
      <SEO title="Web Development, App & AI Solutions" description="CodeViora is an IT services & AI company offering web development, web design, app development, SEO, digital marketing, and AI automation. We build digital products that drive real results." canonical="/" />

      {/* ── HERO ── */}
      <section className="bg-grid-black pt-24 pb-20 md:pt-36 md:pb-32 relative overflow-hidden">
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-label-inv animate-fade-in">IT Services & AI Solutions</span>
              <h1 className="text-5xl sm:text-6xl md:text-7xl text-white leading-[1.05] mt-3 animate-fade-up" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>
                WE BUILD<br/>
                <span className="text-white/35"><Typewriter texts={['WEBSITES.','MOBILE APPS.','AI TOOLS.','BRANDS.','SOLUTIONS.']} /></span>
              </h1>
              <p className="text-white/50 text-lg leading-relaxed mt-6 max-w-lg animate-fade-up" style={{animationDelay:'0.1s'}}>
                Web development, design, apps, SEO, digital marketing & AI automation — your end-to-end IT partner for the modern digital world.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-10 animate-fade-up" style={{animationDelay:'0.2s'}}>
                <Link to="/services" className="btn-primary-inv">Explore Services </Link>
                <Link to="/contact" className="btn-outline-inv">Get Free Quote</Link>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-14 pt-8 border-t border-white/10 animate-fade-up" style={{animationDelay:'0.3s'}}>
                {STATS.map(({value,suffix,label}) => (
                  <div key={label}>
                    <p className="text-2xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}><AnimatedCounter value={value} suffix={suffix} /></p>
                    <p className="text-xs text-white/35 mt-0.5 leading-tight">{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-fade-in" style={{animationDelay:'0.25s'}}><AIEstimator /></div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <section className="bg-pure-black border-y border-white/10 py-4 overflow-hidden" aria-label="Services">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS,...MARQUEE_ITEMS].map((item,i) => (
            <span key={i} className="flex items-center gap-4 px-6 text-white/35 font-medium text-xs tracking-widest uppercase whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-white/20 flex-shrink-0" aria-hidden="true"/>
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* ── SERVICES — WHITE GRID ── */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="section-label">What We Do</span>
              <h2 className="section-title mt-1">IT SERVICES & AI BUILT FOR GROWTH.</h2>
              <p className="section-sub">We cover the full digital stack — design, development, SEO, marketing & AI automation — so you don't need five different agencies.</p>
              <div className="flex flex-col gap-3 mt-8">
                {SERVICES.map(({icon,title}) => (
                  <div key={title} className="flex items-center gap-3 text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white dark:text-white transition-colors">
                    <span>{icon}</span><span className="text-sm font-medium">{title}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8"><Link to="/services" className="btn-primary">See All Services</Link></div>
            </div>
            <div className="reveal" style={{transitionDelay:'100ms'}}><ServiceCarousel /></div>
          </div>
        </div>
      </section>

      {/* ── WHY US — BLACK GRID ── */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">Why CodeViora</span>
            <h2 className="section-title-inv">RESULTS OVER PROMISES.</h2>
            <p className="section-sub-inv mx-auto">We don't just deliver work — we deliver outcomes you can measure.</p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-5">
            {WHY_US.map(({icon,title,desc},i) => (
              <div key={title} className="reveal card-black p-7" style={{transitionDelay:`${i*60}ms`}}>
                <span className="text-3xl mb-4 block text-white">{icon}</span>
                <h3 className="text-lg text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Swipe Carousel */}
          <div className="md:hidden">
            <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
              {WHY_US.map(({icon,title,desc}) => (
                <div key={title} className="flex-shrink-0 w-64 card-black p-7 snap-start">
                  <span className="text-3xl mb-4 block text-white">{icon}</span>
                  <h3 className="text-base text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                  <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-white/30 mt-3">← Swipe to see more →</p>
          </div>
        </div>
      </section>

      {/* ── WORK — WHITE GRID ── */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label">Our Work</span>
            <h2 className="section-title">PROJECTS WE'RE PROUD OF.</h2>
          </div>
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PROJECTS_HOME.map((project, i) => (
              <div key={project.label} className="reveal group cursor-pointer" style={{transitionDelay:`${i*60}ms`}} onClick={() => setLightbox({ open: true, index: i })}>
                <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/3] bg-black/5 dark:bg-white/5">
                  <img src={project.url} alt={project.label} className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white dark:bg-[#111] rounded-full flex items-center justify-center text-black dark:text-white transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100 shadow-xl">
                      <ExternalLink size={20} />
                    </div>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-medium text-black dark:text-white font-display mb-1 group-hover:text-[#ff6b2b] transition-colors">{project.label}</h3>
                    <span className="text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">{project.tag}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden relative">
            <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
              {PROJECTS_HOME.map((project, i) => (
                <div key={project.label} className="flex-shrink-0 w-[85vw] max-w-[320px] snap-start group cursor-pointer" onClick={() => setLightbox({ open: true, index: i })}>
                  <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/3] bg-black/5 dark:bg-white/5">
                    <img src={project.url} alt={project.label} className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white dark:bg-[#111] rounded-full flex items-center justify-center text-black dark:text-white transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100 shadow-xl">
                        <ExternalLink size={20} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-medium text-black dark:text-white font-display mb-1 group-hover:text-[#ff6b2b] transition-colors">{project.label}</h3>
                      <span className="text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">{project.tag}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-black/40 dark:text-white/40 mt-3">← Swipe to see more →</p>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(0,0,0,0.95)'}} onClick={closeImg}>
          <button onClick={closeImg} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium">{lightbox.index + 1} / {PROJECTS_HOME.length}</div>
          <button onClick={e => { e.stopPropagation(); prevImg(); }} className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="max-w-4xl max-h-[80vh] mx-16 md:mx-24" onClick={e => e.stopPropagation()}>
            <img src={PROJECTS_HOME[lightbox.index].url} alt={PROJECTS_HOME[lightbox.index].label} className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" />
            <p className="text-center text-white/50 text-sm mt-4">{PROJECTS_HOME[lightbox.index].label} — {PROJECTS_HOME[lightbox.index].tag}</p>
          </div>
          <button onClick={e => { e.stopPropagation(); nextImg(); }} className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {PROJECTS_HOME.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: i })); }} className={`h-1.5 rounded-full transition-all duration-300 ${i === lightbox.index ? 'bg-white dark:bg-[#111] w-6' : 'bg-white/30 w-3'}`} aria-label={`Go to ${i + 1}`} />
            ))}
          </div>
        </div>
      )}

      {/* ── TESTIMONIALS — BLACK GRID ── */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="section-label-inv">Testimonials</span>
              <h2 className="section-title-inv">CLIENTS SPEAK FOR THEMSELVES.</h2>
              <p className="section-sub-inv">Don't take our word for it. Here's what our clients say.</p>
              <div className="flex items-center gap-8 mt-10">
                <div><p className="text-4xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>25+</p><p className="text-white/30 text-xs mt-1">Happy clients</p></div>
                <div className="w-px h-10 bg-white/10" aria-hidden="true"/>
                <div><p className="text-4xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>98%</p><p className="text-white/30 text-xs mt-1">Satisfaction</p></div>
                <div className="w-px h-10 bg-white/10" aria-hidden="true"/>
                <div>
                  <div className="flex gap-0.5 mb-1">{[...Array(5)].map((_,i)=><svg key={i} width="12" height="12" viewBox="0 0 14 14" fill="white"><path d="M7 1l1.5 3.5L12 5l-2.5 2.5.5 3.5L7 9.5 4 11l.5-3.5L2 5l3.5-.5L7 1z"/></svg>)}</div>
                  <p className="text-white/30 text-xs">5.0 rating</p>
                </div>
              </div>
            </div>
            <div className="reveal" style={{transitionDelay:'100ms'}}><TestimonialCarousel /></div>
          </div>
        </div>
      </section>

      {/* ── BLOG — WHITE GRID ── */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12 reveal">
            <div><span className="section-label">Blog</span><h2 className="section-title">INSIGHTS & UPDATES.</h2></div>
            <Link to="/blog" className="btn-outline text-sm whitespace-nowrap">All Posts →</Link>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            {latestBlogs.map(({slug,title,excerpt,category,date,readTime},i) => (
              <Link key={slug} to={`/blog/${slug}`} className="reveal card-white group overflow-hidden flex flex-col" style={{transitionDelay:`${i*80}ms`}}>
                <div className="h-44 overflow-hidden rounded-t-2xl">
                  <img src={BLOG_IMAGES[category] || FALLBACK_IMAGE} alt={title} className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-black dark:text-white bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-2.5 py-1 rounded-full">{category}</span>
                    <span className="text-xs text-black/30 dark:text-white/30">{readTime}</span>
                  </div>
                  <h3 className="font-medium text-black dark:text-white leading-snug mb-2 group-hover:text-[#ff6b2b] dark:group-hover:text-[#ff6b2b] transition-colors line-clamp-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                  <p className="text-sm text-black/50 dark:text-white/50 leading-relaxed flex-1 line-clamp-2">{excerpt}</p>
                  <p className="text-xs text-black/25 dark:text-white/25 mt-4">{date}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile Swipe Carousel */}
          <div className="md:hidden">
            <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
              {latestBlogs.map(({slug,title,excerpt,category,date,readTime}) => (
                <Link key={slug} to={`/blog/${slug}`} className="flex-shrink-0 w-72 card-white group overflow-hidden snap-start flex flex-col">
                  <div className="h-44 overflow-hidden rounded-t-2xl">
                    <img src={BLOG_IMAGES[category] || FALLBACK_IMAGE} alt={title} className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-black dark:text-white bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-2.5 py-1 rounded-full">{category}</span>
                      <span className="text-xs text-black/30 dark:text-white/30">{readTime}</span>
                    </div>
                    <h3 className="font-medium text-black dark:text-white leading-snug mb-2 group-hover:text-[#ff6b2b] dark:group-hover:text-[#ff6b2b] transition-colors line-clamp-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                    <p className="text-xs text-black/25 dark:text-white/25 mt-auto">{date}</p>
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-center text-xs text-black/30 dark:text-white/30 mt-3">← Swipe to see more →</p>
          </div>
        </div>
      </section>

      {/* ── PRICING — BLACK GRID ── */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">Pricing</span>
            <h2 className="section-title-inv">TRANSPARENT PRICING.<br/>NO SURPRISES.</h2>
            <p className="section-sub-inv mx-auto">Simple, honest pricing based on your project scope. Pick what fits your budget — we'll make it work.</p>
          </div>
          {(() => {
            const plans = [
              {range:'Starter',inr:'Under ₹15K',usd:'$180',eur:'€160',desc:'Perfect for small businesses, landing pages, and personal brands getting online.',features:['Single page website','Mobile responsive','Basic SEO setup','Contact form','1 revision round','7-day delivery'],highlight:false},
              {range:'Growth',inr:'₹15K – ₹40K',usd:'$180 – $480',eur:'€160 – €440',desc:'Ideal for growing businesses needing a full website, app, or marketing campaign.',features:['Multi-page website','CMS integration','On-page SEO','Google Analytics','3 revision rounds','14-day delivery'],highlight:true},
              {range:'Professional',inr:'₹40K – ₹1L',usd:'$480 – $1.2K',eur:'€440 – €1.1K',desc:'For businesses ready to scale with custom web apps, e-commerce, or full campaigns.',features:['Custom web app','E-commerce ready','Advanced SEO','Ad campaigns setup','Unlimited revisions','30-day delivery'],highlight:false},
              {range:'Enterprise',inr:'₹1L+',usd:'$1.2K+',eur:'€1.1K+',desc:'Full-scale IT solutions — complex platforms to ongoing digital marketing retainers.',features:['Full platform build','Mobile app included','SEO + Paid Ads','Dedicated manager','Priority support','Custom timeline'],highlight:false},
            ];
            const PlanCard = ({range,inr,usd,eur,desc,features,highlight},i) => (
              <div key={range} className={`flex-shrink-0 flex flex-col rounded-2xl border transition-all duration-300 relative overflow-hidden md:flex-shrink md:w-auto w-72 snap-start ${highlight?'bg-white dark:bg-[#111] border-white':'bg-white/5 dark:bg-white/5 border-white/10 hover:border-white/30'}`}>
                {highlight && <div className="absolute top-0 left-0 right-0 h-1 bg-black" />}
                {highlight && <div className="absolute top-3 right-3"><span className="text-xs font-medium bg-black text-white px-3 py-1 rounded-full">Most Popular</span></div>}
                <div className={`p-7 flex-1 flex flex-col ${highlight?'text-black dark:text-white':'text-white'}`}>
                  <p className={`text-xs font-medium tracking-widest uppercase mb-4 ${highlight?'text-black/40 dark:text-white/40':'text-white/30'}`}>{range}</p>
                  <p className={`text-3xl leading-none mb-2 ${highlight?'text-black dark:text-white':'text-white'}`} style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{inr}</p>
                  <div className={`flex items-center gap-2 text-xs mb-6 ${highlight?'text-black/40 dark:text-white/40':'text-white/30'}`}><span>{usd}</span><span>·</span><span>{eur}</span></div>
                  <p className={`text-sm leading-relaxed mb-7 ${highlight?'text-black/60 dark:text-white/60':'text-white/40'}`}>{desc}</p>
                  <ul className="flex flex-col gap-3 flex-1">
                    {features.map(f => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${highlight?'bg-black text-white':'bg-white/10 text-white'}`}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                        <span className={highlight?'text-black/70 dark:text-white/70':'text-white/50'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link to="/contact" className={`w-full flex items-center justify-center gap-2 font-medium px-5 py-3 rounded-xl text-sm transition-all duration-200 ${highlight?'bg-black text-white hover:bg-black/80 dark:bg-white/80':'bg-white/10 text-white border border-white/10 hover:bg-white dark:bg-[#111] hover:text-black dark:hover:text-white dark:text-white'}`}>
                      Get Started <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                  </div>
                </div>
              </div>
            );
            return (
              <>
                <div className="hidden md:grid md:grid-cols-4 gap-5">{plans.map((p,i) => PlanCard(p,i))}</div>
                <div className="md:hidden">
                  <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>{plans.map((p,i) => PlanCard(p,i))}</div>
                  <p className="text-center text-xs text-white/30 mt-3">← Swipe to see plans →</p>
                </div>
              </>
            );
          })()}
          <p className="reveal text-center text-white/30 text-xs mt-10">
            * All prices are estimates. Final quote depends on project complexity. <Link to="/contact" className="text-white/60 hover:text-white underline underline-offset-2">Contact us</Link> for a free custom quote.
          </p>
        </div>
      </section>

      {/* ── CTA — WHITE ── */}
      <section className="bg-grid-white py-20 border-t border-black/10 dark:border-white/10">
        <div className="container-max text-center reveal">
          <h2 className="text-4xl md:text-6xl text-black dark:text-white leading-tight mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>READY TO BUILD<br/>SOMETHING GREAT?</h2>
          <p className="text-black/50 dark:text-white/50 text-lg mb-8 max-w-xl mx-auto">Tell us about your project. We'll respond within 24 hours with a free consultation.</p>
          <Link to="/contact" className="btn-primary">
            Start Your Project
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M3 9H15M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Link>
        </div>
      </section>
    </>
  );
}
